import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useGridStore, CellChange } from '@/stores/gridStore';
import { useUpdateTask, useCreateTask, useDeleteTask, taskKeys } from './useTasks';
import { useUpdateColumn, useCreateColumn, useDeleteColumn, columnKeys } from './useColumns';
import { useCreateDependency, useDeleteDependency, dependencyKeys } from './useDependencies';
import { api } from '@/lib/api';

interface Task {
  id: string;
  [key: string]: unknown;
}

interface Column {
  id: string;
  [key: string]: unknown;
}

export function useOptimisticUpdates(sheetId: string) {
  const queryClient = useQueryClient();
  const pushChange = useGridStore((state) => state.pushChange);
  const undo = useGridStore((state) => state.undo);
  const redo = useGridStore((state) => state.redo);

  const updateTaskMutation = useUpdateTask(sheetId);
  const createTaskMutation = useCreateTask(sheetId);
  const deleteTaskMutation = useDeleteTask(sheetId);

  const updateColumnMutation = useUpdateColumn(sheetId);
  const createColumnMutation = useCreateColumn(sheetId);
  const deleteColumnMutation = useDeleteColumn(sheetId);

  const createDependencyMutation = useCreateDependency(sheetId);
  const deleteDependencyMutation = useDeleteDependency(sheetId);

  // Keep track of pending debounced updates
  const pendingUpdates = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Debounced cell update - waits before sending to API
  const debouncedUpdateCell = useCallback(
    (taskId: string, field: string, value: unknown, oldValue: unknown) => {
      const key = `${taskId}:${field}`;

      // Optimistic update — immediately update the cache so the cell shows the new value
      const queryCache = queryClient.getQueryCache();
      const matchingQuery = queryCache.findAll({ queryKey: taskKeys.bySheet(sheetId) })[0];
      if (matchingQuery) {
        const cacheKey = matchingQuery.queryKey;
        const tasks = queryClient.getQueryData<Task[]>(cacheKey);
        if (tasks) {
          queryClient.setQueryData<Task[]>(
            cacheKey,
            tasks.map((t) => {
              if (t.id !== taskId) return t;
              if (field.startsWith('customFields.')) {
                const columnId = field.replace('customFields.', '');
                const customFields = { ...(t.customFields as Record<string, unknown> || {}) };
                customFields[columnId] = value;
                return { ...t, customFields };
              }
              return { ...t, [field]: value };
            }),
          );
        }
      }

      // Clear any pending update for this cell
      const existing = pendingUpdates.current.get(key);
      if (existing) {
        clearTimeout(existing);
      }

      // Fire API call — use short debounce for text fields, immediate for others
      const isTextField = field === 'name' || field === 'description';
      const delay = isTextField ? 500 : 0; // Text debounces, dates/dropdowns fire immediately

      const timeoutId = setTimeout(() => {
        const isCustomField = field.startsWith('customFields.');

        if (isCustomField) {
          const columnId = field.replace('customFields.', '');
          api
            .patch(`/tasks/${taskId}/cells/${columnId}`, { value })
            .then(() => {
              pushChange([
                {
                  type: 'cell',
                  taskId,
                  field,
                  oldValue,
                  newValue: value,
                },
              ]);
            })
            .catch((error) => {
              console.error('Failed to update custom field cell:', error);
              queryClient.invalidateQueries({ queryKey: taskKeys.bySheet(sheetId) });
            })
            .finally(() => {
              pendingUpdates.current.delete(key);
            });
          return;
        }

        updateTaskMutation.mutate(
          { taskId, updates: { [field]: value } },
          {
            onSuccess: () => {
              pushChange([
                {
                  type: 'cell',
                  taskId,
                  field,
                  oldValue,
                  newValue: value,
                },
              ]);
            },
          }
        );
        pendingUpdates.current.delete(key);
      }, delay);

      pendingUpdates.current.set(key, timeoutId);

      // Immediately update the local cache for responsive UI
      const tasks = queryClient.getQueryData<Task[]>(taskKeys.list(sheetId));
      if (tasks) {
        queryClient.setQueryData<Task[]>(
          taskKeys.list(sheetId),
          tasks.map((t) =>
            t.id === taskId
              ? field.startsWith('customFields.')
                ? {
                    ...t,
                    customFields: {
                      ...((t.customFields as Record<string, unknown> | undefined) ?? {}),
                      [field.replace('customFields.', '')]: value,
                    },
                  }
                : { ...t, [field]: value }
              : t
          )
        );
      }
    },
    [sheetId, queryClient, updateTaskMutation, pushChange]
  );

  // Immediate cell update - sends to API immediately
  const updateCellImmediate = useCallback(
    async (taskId: string, field: string, value: unknown, oldValue: unknown) => {
      await updateTaskMutation.mutateAsync({ taskId, updates: { [field]: value } });
      pushChange([
        {
          type: 'cell',
          taskId,
          field,
          oldValue,
          newValue: value,
        },
      ]);
    },
    [updateTaskMutation, pushChange]
  );

  // Batch update multiple cells
  const updateCellsBatch = useCallback(
    async (
      updates: Array<{ taskId: string; field: string; value: unknown; oldValue: unknown }>
    ) => {
      const changes: CellChange[] = [];
      const taskUpdates = new Map<string, Record<string, unknown>>();

      // Group updates by task
      for (const update of updates) {
        const existing = taskUpdates.get(update.taskId) || {};
        existing[update.field] = update.value;
        taskUpdates.set(update.taskId, existing);

        changes.push({
          type: 'cell',
          taskId: update.taskId,
          field: update.field,
          oldValue: update.oldValue,
          newValue: update.value,
        });
      }

      // Execute all updates
      const promises = Array.from(taskUpdates.entries()).map(([taskId, taskUpdate]) =>
        updateTaskMutation.mutateAsync({ taskId, updates: taskUpdate })
      );

      await Promise.all(promises);
      pushChange(changes);
    },
    [updateTaskMutation, pushChange]
  );

  // Handle undo action
  const handleUndo = useCallback(async () => {
    const changes = undo();
    if (!changes) return;

    // Apply reverse changes
    for (const change of changes) {
      switch (change.type) {
        case 'cell': {
          const cellChange = change as CellChange;
          await updateTaskMutation.mutateAsync({
            taskId: cellChange.taskId,
            updates: { [cellChange.field]: cellChange.oldValue },
          });
          break;
        }
        case 'task_create': {
          // Delete the created task
          await deleteTaskMutation.mutateAsync({
            taskId: change.taskId,
            deleteSubtasks: false,
          });
          break;
        }
        case 'task_delete': {
          // Re-create the deleted task
          await createTaskMutation.mutateAsync(change.task as Parameters<typeof createTaskMutation.mutateAsync>[0]);
          break;
        }
        case 'column': {
          // Revert column change
          await updateColumnMutation.mutateAsync({
            columnId: change.columnId,
            updates: { [change.field]: change.oldValue },
          });
          break;
        }
        case 'dependency': {
          // Handle dependency changes
          if (change.action === 'create') {
            await deleteDependencyMutation.mutateAsync(change.dependencyId);
          } else if (change.action === 'delete') {
            await createDependencyMutation.mutateAsync(change.data as Parameters<typeof createDependencyMutation.mutateAsync>[0]);
          }
          break;
        }
        case 'batch': {
          // Recursively handle batch changes
          for (const subChange of change.changes) {
            if (subChange.type === 'cell') {
              await updateTaskMutation.mutateAsync({
                taskId: subChange.taskId,
                updates: { [subChange.field]: subChange.oldValue },
              });
            }
          }
          break;
        }
      }
    }

    // Invalidate queries to refresh
    queryClient.invalidateQueries({ queryKey: taskKeys.list(sheetId) });
    queryClient.invalidateQueries({ queryKey: columnKeys.list(sheetId) });
    queryClient.invalidateQueries({ queryKey: dependencyKeys.bySheet(sheetId) });
  }, [
    undo,
    sheetId,
    queryClient,
    updateTaskMutation,
    deleteTaskMutation,
    createTaskMutation,
    updateColumnMutation,
    createDependencyMutation,
    deleteDependencyMutation,
  ]);

  // Handle redo action
  const handleRedo = useCallback(async () => {
    const changes = redo();
    if (!changes) return;

    // Apply forward changes
    for (const change of changes) {
      switch (change.type) {
        case 'cell': {
          const cellChange = change as CellChange;
          await updateTaskMutation.mutateAsync({
            taskId: cellChange.taskId,
            updates: { [cellChange.field]: cellChange.newValue },
          });
          break;
        }
        case 'task_create': {
          // Re-create the task
          await createTaskMutation.mutateAsync(change.task as Parameters<typeof createTaskMutation.mutateAsync>[0]);
          break;
        }
        case 'task_delete': {
          // Re-delete the task
          await deleteTaskMutation.mutateAsync({
            taskId: change.taskId,
            deleteSubtasks: false,
          });
          break;
        }
        case 'column': {
          // Apply column change
          await updateColumnMutation.mutateAsync({
            columnId: change.columnId,
            updates: { [change.field]: change.newValue },
          });
          break;
        }
        case 'dependency': {
          if (change.action === 'create') {
            await createDependencyMutation.mutateAsync(change.data as Parameters<typeof createDependencyMutation.mutateAsync>[0]);
          } else if (change.action === 'delete') {
            await deleteDependencyMutation.mutateAsync(change.dependencyId);
          }
          break;
        }
        case 'batch': {
          for (const subChange of change.changes) {
            if (subChange.type === 'cell') {
              await updateTaskMutation.mutateAsync({
                taskId: subChange.taskId,
                updates: { [subChange.field]: subChange.newValue },
              });
            }
          }
          break;
        }
      }
    }

    // Invalidate queries to refresh
    queryClient.invalidateQueries({ queryKey: taskKeys.list(sheetId) });
    queryClient.invalidateQueries({ queryKey: columnKeys.list(sheetId) });
    queryClient.invalidateQueries({ queryKey: dependencyKeys.bySheet(sheetId) });
  }, [
    redo,
    sheetId,
    queryClient,
    updateTaskMutation,
    deleteTaskMutation,
    createTaskMutation,
    updateColumnMutation,
    createDependencyMutation,
    deleteDependencyMutation,
  ]);

  // Create task with history tracking
  const createTask = useCallback(
    async (taskData: Parameters<typeof createTaskMutation.mutateAsync>[0]) => {
      const result = await createTaskMutation.mutateAsync(taskData);
      pushChange([
        {
          type: 'task_create',
          taskId: result.id,
          task: taskData,
        },
      ]);
      return result;
    },
    [createTaskMutation, pushChange]
  );

  // Delete task with history tracking
  const deleteTask = useCallback(
    async (taskId: string, deleteSubtasks = false) => {
      // Get the task data before deleting for undo
      const tasks = queryClient.getQueryData<Task[]>(taskKeys.list(sheetId));
      const taskToDelete = tasks?.find((t) => t.id === taskId);
      const taskIndex = tasks?.findIndex((t) => t.id === taskId) ?? -1;

      await deleteTaskMutation.mutateAsync({ taskId, deleteSubtasks });

      if (taskToDelete) {
        pushChange([
          {
            type: 'task_delete',
            taskId,
            task: taskToDelete,
            index: taskIndex,
          },
        ]);
      }
    },
    [deleteTaskMutation, pushChange, queryClient, sheetId]
  );

  // Create column with history tracking
  const createColumn = useCallback(
    async (columnData: Parameters<typeof createColumnMutation.mutateAsync>[0]) => {
      const result = await createColumnMutation.mutateAsync(columnData);
      pushChange([
        {
          type: 'column',
          columnId: result.id,
          field: '_create',
          oldValue: null,
          newValue: columnData,
        },
      ]);
      return result;
    },
    [createColumnMutation, pushChange]
  );

  // Delete column with history tracking
  const deleteColumn = useCallback(
    async (columnId: string) => {
      const columns = queryClient.getQueryData<Column[]>(columnKeys.list(sheetId));
      const columnToDelete = columns?.find((c) => c.id === columnId);

      await deleteColumnMutation.mutateAsync(columnId);

      if (columnToDelete) {
        pushChange([
          {
            type: 'column',
            columnId,
            field: '_delete',
            oldValue: columnToDelete,
            newValue: null,
          },
        ]);
      }
    },
    [deleteColumnMutation, pushChange, queryClient, sheetId]
  );

  // Create dependency with history tracking
  const createDependency = useCallback(
    async (data: Parameters<typeof createDependencyMutation.mutateAsync>[0]) => {
      const result = await createDependencyMutation.mutateAsync(data);
      pushChange([
        {
          type: 'dependency',
          action: 'create',
          dependencyId: result.id,
          data,
        },
      ]);
      return result;
    },
    [createDependencyMutation, pushChange]
  );

  // Delete dependency with history tracking
  const deleteDependency = useCallback(
    async (dependencyId: string) => {
      await deleteDependencyMutation.mutateAsync(dependencyId);
      pushChange([
        {
          type: 'dependency',
          action: 'delete',
          dependencyId,
          data: null,
        },
      ]);
    },
    [deleteDependencyMutation, pushChange]
  );

  return {
    // Cell updates
    updateCell: debouncedUpdateCell,
    updateCellImmediate,
    updateCellsBatch,

    // Task operations
    createTask,
    deleteTask,

    // Column operations
    createColumn,
    deleteColumn,

    // Dependency operations
    createDependency,
    deleteDependency,

    // Undo/Redo
    undo: handleUndo,
    redo: handleRedo,

    // Loading states
    isUpdating: updateTaskMutation.isPending,
    isCreatingTask: createTaskMutation.isPending,
    isDeletingTask: deleteTaskMutation.isPending,
    isCreatingColumn: createColumnMutation.isPending,
    isDeletingColumn: deleteColumnMutation.isPending,
  };
}

export default useOptimisticUpdates;
